// teacherHomeMap.js
// Pure mapping of the /home/teacher-summary (B1) response into the shapes
// the teacher home modules (WP5) consume — no React, no network.
// See docs/superpowers/specs/2026-09-16-home-redesign.md §11 "WP3 (revised)".
import { HOME } from "../homeTheme";

const identity = (s) => s;

// action.type -> the row's action label (run through `t` before use, since
// HomeRow renders `action.label` verbatim with no translation of its own).
const ACTION_LABELS = {
  open_test: "View responses",
  open_mastery: "Open mastery",
  open_topic: "Open topic",
  message_student: "Message",
  grade_test: "Grade",
};

// action.type -> which field of the action payload to hand the handler.
const ACTION_PAYLOAD_KEYS = {
  open_test: "test_id",
  open_mastery: "source_topic_id",
  open_topic: "source_topic_id",
  message_student: "email",
  grade_test: "test_id",
};

/**
 * Map one `attention[].action` into a HomeRow `action` prop.
 * Returns null when the action type is unrecognized or no handler was
 * supplied for it (per spec: "missing handler -> action: null").
 */
export function mapAttentionAction(action, handlers = {}, t = identity) {
  const type = action?.type;
  if (!type) return null;

  const label = ACTION_LABELS[type];
  const handler = handlers[type];
  if (!label || typeof handler !== "function") return null;

  const payloadKey = ACTION_PAYLOAD_KEYS[type];
  const payload = payloadKey ? action[payloadKey] : undefined;

  return { label: t(label), onPress: () => handler(payload) };
}

/**
 * Localize one attention[] item's title/subtitle via its `i18n` payload
 * (F3d): `{ title: {key, params}, subtitle: {key, params} | null }`. Falls
 * back to the raw English `title`/`subtitle` when `i18n` (or one of its two
 * fields) is absent, so an item built before F3d shipped -- or a field the
 * builder left untranslated, like the inactive/grading `subtitle: null`
 * cases -- still renders. `to_review[]` has no `i18n` and must never be
 * passed through here; see mapToReview.
 */
export function localizeItem(item, t = identity) {
  const i18n = item?.i18n;
  const title = i18n?.title ? t(i18n.title.key, i18n.title.params) : item?.title;
  const subtitle = i18n?.subtitle ? t(i18n.subtitle.key, i18n.subtitle.params) : item?.subtitle;
  return { ...item, title, subtitle };
}

/** Map one B1 attention[] item into a HomeRow-shaped row. */
export function mapAttentionItem(item, handlers, t = identity) {
  const localized = localizeItem(item, t);
  return {
    tag: item?.tag ? { label: t(item.tag.label), tone: item.tag.tone } : null,
    title: localized.title,
    subtitle: localized.subtitle,
    action: mapAttentionAction(item?.action, handlers, t),
  };
}

/** Map the attention[] list, capped at HOME.maxAttention. */
export function mapAttention(attention, handlers = {}, t = identity) {
  const list = Array.isArray(attention) ? attention : [];
  return list.slice(0, HOME.maxAttention).map((item) => mapAttentionItem(item, handlers, t));
}

/** Map `week_stats` -> `{ active, total, avgMastery, questions, quizzes }`. */
export function mapWeekStats(weekStats) {
  const stats = weekStats || {};
  return {
    active: stats.active_students ?? 0,
    total: stats.total_students ?? 0,
    avgMastery: stats.avg_mastery ?? null,
    questions: stats.ai_questions ?? 0,
    quizzes: stats.quiz_attempts ?? 0,
  };
}

/**
 * Map one `to_review[]` item ->
 * `{ test: {id, title, end_time}, submitted, needsMark, notSubmitted, closes }`.
 */
export function mapToReviewItem(item) {
  return {
    test: { id: item?.test_id, title: item?.title, end_time: item?.end_time ?? null },
    submitted: item?.submitted ?? 0,
    needsMark: item?.needs_mark ?? 0,
    notSubmitted: Array.isArray(item?.not_started) ? item.not_started.length : 0,
    closes: item?.end_time ?? null,
  };
}

/** Map the `to_review[]` list, capped at HOME.maxToReview. */
export function mapToReview(toReview) {
  const list = Array.isArray(toReview) ? toReview : [];
  return list.slice(0, HOME.maxToReview).map(mapToReviewItem);
}

/** `students[]` passes through unchanged, defensively defaulted to []. */
export function mapStudents(students) {
  return Array.isArray(students) ? students : [];
}

/** Map one `needs_reteaching[]` item (F3c) -> the NeedsReteachingList shape. */
export function mapNeedsReteachingItem(item) {
  return {
    topic: item?.topic,
    name: item?.name,
    classAverage: item?.class_average,
    classAveragePercent: item?.class_average_percent ?? null,
    below: item?.below,
    evidence: item?.evidence,
    delta: item?.delta ?? null,
    deltaPoints: item?.delta_points ?? null,
  };
}

/** Map the `needs_reteaching[]` list (already capped at 3, worst-first, server-side). */
export function mapNeedsReteaching(needsReteaching) {
  const list = Array.isArray(needsReteaching) ? needsReteaching : [];
  return list.map(mapNeedsReteachingItem);
}

/**
 * Pure mapping of the whole `/home/teacher-summary` response into the shape
 * `useTeacherHomeData` returns (minus `loading`/`error`, which are request
 * lifecycle state owned by the hook, not the response).
 */
export function mapSummary(json, handlers = {}, t = identity) {
  const data = json || {};
  return {
    students: mapStudents(data.students),
    attention: mapAttention(data.attention, handlers, t),
    needsReteaching: mapNeedsReteaching(data.needs_reteaching),
    weekStats: mapWeekStats(data.week_stats),
    toReview: mapToReview(data.to_review),
    window: data.window ?? null,
  };
}
